import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Banknote, CheckCheck, Diamond, Heart, Hourglass, Lock, Activity, Send, ShieldCheck, TrendingUp, Undo2, Users } from "lucide-react";

import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { AdminPage, Card, ChipRow, Gate, H, HBars, StatCard, StatGrid } from "@/components/admin-ui";
import { ErrorState, Loading } from "@/components/ui";

export default function AdminAnalytics() {
  return <Gate perm="analytics" testId="admin-analytics-forbidden"><Inner /></Gate>;
}

function Inner() {
  const [period, setPeriod] = useState("day");
  const { data: a, isLoading, isError, error, refetch } = useQuery({ queryKey: ["admin-analytics"], queryFn: () => api("/admin/analytics") });
  if (isLoading) return <Loading />;
  if (isError || !a) return <ErrorState testId="admin-analytics-error" message={error?.message ?? ""} onRetry={() => refetch()} />;
  const series = (period === "day" ? a.registrations_per_day : a.registrations_per_week).map((d) => ({ ...d, short: period === "day" ? d.label.slice(8) : d.label.slice(5) }));
  return (
    <AdminPage title="Analytics" testId="admin-analytics-screen">
      <StatGrid>
        <StatCard testId="stat-members" icon={Users} label="Members" value={a.members_total} />
        <StatCard testId="stat-verified" icon={ShieldCheck} tone="gold" label="Verified" value={a.verified} />
        <StatCard testId="stat-active" icon={Activity} label="Active (7 days)" value={a.active_7d} />
        <StatCard testId="stat-requests" icon={Send} label="Requests sent" value={a.requests_sent} />
        <StatCard testId="stat-acceptance" icon={CheckCheck} label={`Accepted (${a.requests_accepted})`} value={`${a.acceptance_rate}%`} />
        <StatCard testId="stat-connections" icon={Heart} label={`Connections (${a.connections_active} active)`} value={a.connections_total} />
        <StatCard testId="stat-revenue" icon={Banknote} label={`Payments (${a.payments_success_count})`} value={fmtMoney(a.payments_success_total, a.currency)} />
        <StatCard testId="stat-refunded" icon={Undo2} label="Refunded" value={fmtMoney(a.refunded_total, a.currency)} />
      </StatGrid>
      <H>Membership</H>
      <StatGrid>
        <StatCard testId="stat-active-trials" icon={Hourglass} label="Active trials" value={a.active_trials} />
        <StatCard testId="stat-active-paid" icon={Diamond} label="Active paid members" value={a.active_paid} />
        <StatCard testId="stat-conversion" icon={TrendingUp} label="Trial → paid conversion" value={`${a.trial_conversion_rate}%`} />
        <StatCard testId="stat-membership-revenue" icon={Banknote} label="Membership revenue" value={fmtMoney(a.membership_revenue, a.currency)} />
        <StatCard testId="stat-expired" icon={Lock} label="Expired (browsing only)" value={a.expired_members} />
      </StatGrid>
      {a.paid_by_plan.length ? <Card testId="analytics-paid-by-plan"><HBars testId="analytics-paid-by-plan-chart" data={a.paid_by_plan} /></Card> : null}
      <H>Registrations</H>
      <Card testId="analytics-registrations">
        <ChipRow options={[{ id: "day", label: "Per day (14d)" }, { id: "week", label: "Per week (8w)" }]} value={period} onChange={setPeriod} testIdPrefix="analytics-period" />
        <div className="mt-4 h-64" data-testid="analytics-registrations-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E2DA" vertical={false} />
              <XAxis dataKey="short" tick={{ fontSize: 11, fill: "#756E69" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#756E69" }} axisLine={false} tickLine={false} width={30} />
              <Tooltip cursor={{ fill: "#F6E3E4" }} labelFormatter={(_, p) => p?.[0]?.payload?.label ?? ""} />
              <Bar dataKey="value" name="Registrations" fill="#6F2633" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <div className="grid gap-3 lg:grid-cols-2">
        <div><H>By mission</H><Card testId="analytics-missions" className="mt-2"><HBars testId="analytics-missions-chart" data={a.by_mission} /></Card></div>
        <div><H>By country</H><Card testId="analytics-countries" className="mt-2"><HBars testId="analytics-countries-chart" data={a.by_country} /></Card></div>
      </div>
    </AdminPage>
  );
}
